import { useState } from 'react';
import { CARDBACKS, EXTRA_EMOTES, FELTS, FRAMES, HERO, HEROES, TITLES } from '@aleaces/engine';
import { Portrait } from '../components/Portrait';
import { equip as equipAction, equipSkin } from '../actions';
import { useProfile } from '../hooks';
import { owns } from '../profile';
import { toast } from '../toast';

type Tab = 'cardback' | 'felt' | 'skin' | 'frame' | 'title' | 'emote';

const TABS: [Tab, string][] = [
  ['cardback', 'Kartenrücken'], ['felt', 'Tische'], ['skin', 'Helden-Skins'], ['frame', 'Rahmen'], ['title', 'Titel'], ['emote', 'Emotes'],
];

export function CollectionScreen() {
  const [tab, setTab] = useState<Tab>('cardback');
  const p = useProfile();

  const equip = (t: 'cardback' | 'felt' | 'frame' | 'title', id: string, name: string, locked: boolean, src: string) => {
    if (locked) { toast('Noch gesperrt', src); return; }
    equipAction(t, id);
    toast('Ausgerüstet', name);
  };

  if (!p) return null;

  return (
    <section className="screen">
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div className="eyebrow">Sammlung</div>
        <h1>Deine Truhe</h1>
      </div>
      <div className="col-tabs">
        {TABS.map(([t, label]) => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>{label}</button>
        ))}
      </div>
      <div className="col-grid">
        {tab === 'cardback' && CARDBACKS.map((c) => {
          const own = owns('cardback', c.id, p);
          return (
            <div key={c.id} className={'col-item' + (own ? '' : ' locked') + (p.cardback === c.id ? ' equipped' : '')}
              onClick={() => equip('cardback', c.id, c.name, !own, c.src)}>
              <div className="preview" style={{ background: c.css, border: '2px solid #e9d7a6' }} />
              <div>{c.name}</div>
              <div className="src">{own ? (p.cardback === c.id ? 'Ausgerüstet' : 'Freigeschaltet') : c.src}</div>
            </div>
          );
        })}
        {tab === 'felt' && FELTS.map((c) => {
          const own = owns('felt', c.id, p);
          return (
            <div key={c.id} className={'col-item' + (own ? '' : ' locked') + (p.felt === c.id ? ' equipped' : '')}
              onClick={() => equip('felt', c.id, c.name, !own, c.src)}>
              <div className="preview felt" style={{ background: `radial-gradient(ellipse at 50% 40%,${c.hi},${c.base} 60%,${c.edge})`, boxShadow: 'inset 0 0 0 3px rgba(226,165,58,.3)' }} />
              <div>{c.name}</div>
              <div className="src">{own ? (p.felt === c.id ? 'Ausgerüstet' : 'Freigeschaltet') : c.src}</div>
            </div>
          );
        })}
        {tab === 'skin' && HEROES.flatMap((h) => h.skins.map((s) => {
          const k = h.id + ':' + s.id;
          const own = owns('skin', k, p);
          const eq = (p.skins[h.id] ?? 'base') === s.id;
          return (
            <div key={k} className={'col-item' + (own ? '' : ' locked') + (eq ? ' equipped' : '')}
              onClick={() => {
                if (!own) { toast('Noch gesperrt', s.id === 'base' ? 'Standard' : 'Premium-Pass'); return; }
                equipSkin(h.id, s.id);
                toast('Ausgerüstet', h.name.split(' ')[0] + ' · ' + s.name);
              }}>
              <Portrait heroId={h.id} skinId={s.id} size={80} />
              <div>{h.name.split(' ')[0]} · {s.name}</div>
              <div className="src">{own ? (eq ? 'Ausgerüstet' : 'Freigeschaltet') : s.id === 'base' ? 'Standard' : 'Premium-Pass'}</div>
            </div>
          );
        }))}
        {tab === 'frame' && FRAMES.map((c) => {
          const own = owns('frame', c.id, p);
          return (
            <div key={c.id} className={'col-item' + (own ? '' : ' locked') + (p.frame === c.id ? ' equipped' : '')}
              onClick={() => equip('frame', c.id, c.name, !own, c.src)}>
              <Portrait heroId={p.hero} skinId={p.skins[p.hero] ?? 'base'} frame={c.id} size={80} />
              <div>{c.name}</div>
              <div className="src">{own ? (p.frame === c.id ? 'Ausgerüstet' : 'Freigeschaltet') : c.src}</div>
            </div>
          );
        })}
        {tab === 'title' && TITLES.map((c) => {
          const own = owns('title', c.id, p);
          return (
            <div key={c.id} className={'col-item' + (own ? '' : ' locked') + (p.title === c.id ? ' equipped' : '')}
              onClick={() => equip('title', c.id, c.name, !own, c.src)}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--gold-hi)', padding: '26px 0' }}>{c.name}</div>
              <div>Titel</div>
              <div className="src">{own ? (p.title === c.id ? 'Ausgerüstet' : 'Freigeschaltet') : c.src}</div>
            </div>
          );
        })}
        {tab === 'emote' && (
          <>
            {(HERO[p.hero] ?? HEROES[0]!).emotes.map((t, i) => (
              <div key={'h' + i} className="col-item">
                <div style={{ fontStyle: 'italic', padding: '20px 4px', fontFamily: 'var(--font-body)', fontSize: 14 }}>„{t}“</div>
                <div>{(HERO[p.hero] ?? HEROES[0]!).name.split(' ')[0]}</div>
                <div className="src">Helden-Emote</div>
              </div>
            ))}
            {EXTRA_EMOTES.map((c) => {
              const own = owns('emote', c.id, p);
              return (
                <div key={c.id} className={'col-item' + (own ? '' : ' locked')}>
                  <div style={{ fontStyle: 'italic', padding: '20px 4px', fontFamily: 'var(--font-body)', fontSize: 14 }}>„{c.text}“</div>
                  <div>Zusatz-Emote</div>
                  <div className="src">{own ? 'Freigeschaltet' : c.src}</div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </section>
  );
}
